import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useUserSession } from './appStateHooks';
import { NavigationEntityType } from './useAINavigationResolver';
import { ISurveyResponseByIdResult } from '@Queries/Surveys';

// Re-export for convenience
export type { NavigationEntityType };

/**
 * Hook for resolving entity paths with async API calls
 * Used by AI assistant components to resolve direct navigation paths to entities
 */
export function useAIEntityPathResolver() {
	const userSession = useUserSession();

	const resolveEntityPath = useCallback(
		async (
			entityType: NavigationEntityType,
			entityId: string,
			parentEntityType?: NavigationEntityType,
			parentEntityId?: string,
			impersonationContext?: Api.IImpersonationContext
		): Promise<string> => {
			// Handle SurveyResponse with the specialized resolver
			if (entityType === 'SurveyResponse') {
				try {
					const surveyResponse = await fetchSurveyResponse(entityId, userSession, impersonationContext);
					return mapSurveyResponseToReportingUrl(surveyResponse);
				} catch (error) {
					console.error('Failed to fetch survey response:', error);
					// Fallback to generic responses page if API call fails
					return `/reporting/surveys/responses/${entityId}`;
				}
			}

			// Handle standard entity types
			switch (entityType) {
				case 'ScheduledMeeting':
				case 'Note':
				case 'PhoneCall':
					if (parentEntityType === 'Contact' && parentEntityId) {
						return `/contacts/people/${parentEntityId}`;
					}
					throw new Error(`Unsupported parent entity type: ${parentEntityType}`);
				case 'Contact':
					return `/contacts/people/${entityId}`;
				default:
					throw new Error(`Unsupported entity type: ${entityType}`);
			}
		},
		[userSession]
	);

	return { resolveEntityPath };
}

// Survey response fetching logic extracted from the resolver
async function fetchSurveyResponse(
	entityId: string,
	userSession: Api.IUserSessionContext,
	impersonationContext?: Api.IImpersonationContext
): Promise<ISurveyResponseByIdResult> {
	return userSession.webServiceHelper.callAsync<ISurveyResponseByIdResult>(
		Api.ImpersonationBroker.composeApiUrl({
			impersonationContext,
			urlPath: `survey/response/${entityId}`,
		}),
		'GET'
	);
}

// Survey response URL mapping logic
function mapSurveyResponseToReportingUrl(surveyResponse: ISurveyResponseByIdResult): string {
	switch (surveyResponse._type) {
		case 'SatisfactionSurveyResponse':
			return `/reporting/surveys/satisfaction/${surveyResponse.surveyId}`;
		case 'EventRegistrationSurveyResponse':
			return `/reporting/surveys/events/${surveyResponse.surveyId}`;
		case 'CustomSurveyResponse':
			return `/reporting/surveys/custom/${surveyResponse.surveyId}`;
		default:
			throw new Error(`Unsupported survey response type: ${surveyResponse._type}`);
	}
}
